/**
 * KonvaDrawingsLayerContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStore, useStorePrimitive)
 * - Reading map drawings, regions, terrain types, and visibility settings
 * - Delegating presentation to the pure KonvaDrawingsLayer component
 *
 * Note: useGridCalculator and useGridService are computation/context hooks
 * (not Zustand store hooks) and remain in the presenter.
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useStore, useStorePrimitive } from '../../hooks/useStore';
import type { MapDrawing, MapRegion, TerrainType, RenderedImageRect, InteractionState } from '../../types/types';
import KonvaDrawingsLayer from './KonvaDrawingsLayer';

interface KonvaDrawingsLayerContainerProps {
  imageRect: RenderedImageRect | null;
  interaction: InteractionState | null;
}

const emptyDrawings: MapDrawing[] = [];
const emptyRegions: MapRegion[] = [];
const emptyTerrainTypes: TerrainType[] = [];

const KonvaDrawingsLayerContainer: React.FC<KonvaDrawingsLayerContainerProps> = ({ imageRect, interaction }) => {
  const mapDrawings = useStore(state => state.session?.map?.mapDrawings) || emptyDrawings;
  const mapRegions = useStore(state => state.session?.map?.mapRegions) || emptyRegions;
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const terrainTypes = useStore(state => state.session?.cache?.terrainTypes) || emptyTerrainTypes;
  const showMapRegions = useStorePrimitive(state => state.settings?.showMapRegions);

  return (
    <KonvaDrawingsLayer
      imageRect={imageRect}
      interaction={interaction}
      mapDrawings={mapDrawings}
      mapRegions={mapRegions}
      activeMapId={activeMapId}
      terrainTypes={terrainTypes}
      showMapRegions={showMapRegions}
    />
  );
};

export default KonvaDrawingsLayerContainer;
